import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useLenis, scrollToId } from "../hooks/useLenis";
import { NoiseOverlay } from "../components/site/NoiseOverlay";
import { Nav } from "../components/site/Nav";
import { Footer } from "../components/site/Footer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useAuth } from "../lib/AuthContext";
import {
  listFilms, recommendedFilms, getFilm, createFilm, buyFilm, likeFilm, getFilmComments, addFilmComment,
  filmVideoUrl, uploadFile, absFileUrl, formatApiError, deleteFilm,
} from "../lib/api";
import { toast } from "sonner";
import {
  Clapperboard, Heart, Eye, MessageCircle, Lock, Play, Coins, Loader2, Send, X,
  Sparkles, Flame, Clock, Copy, Wand2, Film, Plus, Upload, ImagePlus, Share2, TrendingUp, Trash2,
} from "lucide-react";
import { CreatorRow } from "../components/site/CreatorRow";

const shareLink = async (url, title) => {
  try {
    if (navigator.share) { await navigator.share({ title, url }); return; }
    await navigator.clipboard.writeText(url);
    toast.success("Lien copié dans le presse-papier");
  } catch (e) {
    if (e?.name !== "AbortError") {
      try { await navigator.clipboard.writeText(url); toast.success("Lien copié"); } catch { toast.error("Partage impossible."); }
    }
  }
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return new Date(iso).toLocaleDateString("fr-FR");
};
const initials = (n) => (n || "?").slice(0, 2).toUpperCase();

const FilmCard = ({ film, index, onOpen, rank, canManage, onDelete }) => (
  <motion.button
    data-testid={`film-card-${film.id}`}
    onClick={() => onOpen(film)}
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: (index % 6) * 0.04 }}
    className="group relative flex w-[260px] shrink-0 snap-start flex-col text-left sm:w-[300px]"
  >
    <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-blue-500/15 bg-[#0F172A] shadow-[inset_0_0_20px_rgba(0,0,0,0.5)] transition-all duration-300 group-hover:-translate-y-1.5 group-hover:border-blue-400/50 group-hover:shadow-[0_0_30px_rgba(37,99,235,0.25)]">
      {film.thumbnail ? (
        <img src={film.thumbnail} alt={film.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_50%_40%,rgba(37,99,235,0.18),transparent_65%)]">
          <Film size={38} className="text-blue-400/40" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220] via-[#0B1220]/10 to-transparent" />
      {typeof rank === "number" && (
        <span className="absolute -left-1 bottom-1 select-none font-display text-6xl font-800 leading-none text-white/10 [-webkit-text-stroke:1.5px_rgba(96,165,250,0.5)]">{rank}</span>
      )}
      {canManage && (
        <span
          role="button"
          tabIndex={0}
          data-testid={`film-delete-${film.id}`}
          onClick={(e) => { e.stopPropagation(); onDelete(film); }}
          className="absolute right-2.5 bottom-2.5 z-10 inline-flex items-center gap-1 rounded-full border border-red-400/40 bg-[#0B1220]/85 px-2.5 py-1 font-mono text-[8px] uppercase tracking-[0.15em] text-red-300 backdrop-blur-sm transition-colors hover:bg-red-500/25 hover:text-red-100"
        >
          <Trash2 size={10} /> Suppr.
        </span>
      )}
      {!film.unlocked && (
        <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <span className="flex h-11 w-11 items-center justify-center rounded-full border border-blue-400/50 bg-[#0B1220]/85 backdrop-blur-sm">
            <Lock size={16} className="text-blue-200" />
          </span>
        </div>
      )}
      <div className="absolute right-2.5 top-2.5 flex items-center gap-1.5 rounded-full border border-blue-400/40 bg-[#0B1220]/85 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-blue-100 backdrop-blur-sm">
        <Coins size={10} className="text-blue-300" /> {film.watch_credits} cr
      </div>
      {film.has_prompt && (
        <div className="absolute left-2.5 top-2.5 flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/15 px-2 py-1 font-mono text-[8px] uppercase tracking-[0.15em] text-amber-200 backdrop-blur-sm">
          <Wand2 size={9} /> Prompt
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 p-3 font-mono text-[10px] text-[#CBD5E1]">
        <span className="flex items-center gap-1"><Heart size={11} className={film.liked ? "fill-blue-300 text-blue-300" : ""} /> {film.likes}</span>
        <span className="flex items-center gap-1"><Eye size={11} /> {film.views}</span>
        <span className="ml-auto flex items-center gap-1 rounded-full bg-blue-600/90 px-3 py-1 text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          {film.unlocked ? <><Play size={10} /> Voir</> : <><Lock size={10} /> Débloquer</>}
        </span>
      </div>
    </div>
    <h3 className="mt-2.5 line-clamp-1 px-0.5 font-display text-sm font-700 tracking-tight text-white">{film.title}</h3>
    <p className="mt-0.5 line-clamp-1 px-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-[#8AA0C0]">par {film.creator_name}</p>
  </motion.button>
);

const Spotlight = ({ film, onOpen, onPublish }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  // Only the owner/purchaser can preview (paywall preserved). Element is mounted WITHOUT a
  // src so nothing is fetched until the user actually hovers (src is set imperatively).
  const canPreview = !!film && film.unlocked;

  useEffect(() => { setPlaying(false); }, [film && film.id]);

  const enter = () => {
    const v = videoRef.current;
    if (!v) return;
    if (!v.getAttribute("src")) v.setAttribute("src", filmVideoUrl(film.id));
    v.play().catch(() => {});
  };
  const leave = () => {
    const v = videoRef.current;
    if (v) v.pause();
    setPlaying(false);
  };

  if (!film) return null;
  return (
    <motion.div
      data-testid="films-spotlight"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      onMouseEnter={enter}
      onMouseLeave={leave}
      className="group relative overflow-hidden rounded-3xl border border-blue-500/20"
    >
      <div className="absolute inset-0">
        {film.thumbnail ? (
          <img src={film.thumbnail} alt={film.title} className={`h-full w-full object-cover transition-opacity duration-700 ${playing ? "opacity-0" : "opacity-100"}`} />
        ) : (
          <div className="h-full w-full bg-[radial-gradient(circle_at_70%_30%,rgba(37,99,235,0.35),#0B1220_70%)]" />
        )}
        {canPreview && (
          <video
            ref={videoRef}
            data-testid="spotlight-video"
            muted loop playsInline preload="none"
            onPlaying={() => setPlaying(true)}
            onError={() => setPlaying(false)}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${playing ? "opacity-100" : "opacity-0"}`}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1220] via-[#0B1220]/85 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220] via-transparent to-transparent" />
      </div>
      <div className="relative flex min-h-[62vh] flex-col justify-end gap-4 p-7 sm:min-h-[58vh] sm:p-10 lg:min-h-[64vh] lg:p-14">
        <div className="max-w-xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-amber-200">
            <Flame size={12} /> Film à la une
          </span>
          <h1 className="mt-4 font-display text-4xl font-800 leading-none tracking-tighter text-white text-glow sm:text-5xl lg:text-6xl">
            {film.title}
          </h1>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-blue-200/80">
            par {film.creator_name}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-4 font-mono text-xs text-[#CBD5E1]">
            <span className="flex items-center gap-1.5"><Heart size={13} className={film.liked ? "fill-blue-300 text-blue-300" : ""} /> {film.likes}</span>
            <span className="flex items-center gap-1.5"><Eye size={13} /> {film.views}</span>
            <span className="flex items-center gap-1.5"><MessageCircle size={13} /> {film.comments_count}</span>
            {film.has_prompt && <span className="flex items-center gap-1.5 text-amber-300"><Wand2 size={13} /> Prompt à vendre</span>}
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button data-testid="spotlight-play" onClick={() => onOpen(film)}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white cta-glow transition-all duration-300 hover:bg-blue-500">
              {film.unlocked ? <Play size={15} /> : <Lock size={14} />}
              {film.unlocked ? "Regarder" : `Débloquer · ${film.watch_credits} cr`}
            </button>
            <button data-testid="spotlight-publish" onClick={onPublish}
              className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-[#0B1220]/60 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-blue-100 backdrop-blur-sm transition-all duration-300 hover:border-blue-400/50 hover:text-white">
              <Plus size={14} /> Publier un film
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Comments = ({ filmId }) => {
  const { user, setAuthOpen } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { getFilmComments(filmId).then(({ data }) => setItems(data)).catch((e) => console.error("load comments failed", e)); }, [filmId]);

  const send = async () => {
    if (!user) return setAuthOpen(true);
    if (!text.trim()) return;
    try {
      setBusy(true);
      const { data } = await addFilmComment(filmId, text.trim());
      setItems((c) => [data, ...c]);
      setText("");
    } catch { toast.error("Commentaire impossible."); } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 border-t border-blue-500/10 pt-5" data-testid="film-comments">
      <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-blue-300/70">Commentaires ({items.length})</p>
      <div className="mb-4 flex gap-2">
        <input
          data-testid="film-comment-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={user ? "Ajouter un commentaire…" : "Connectez-vous pour commenter"}
          onFocus={() => { if (!user) setAuthOpen(true); }}
          className="flex-1 rounded-full border border-blue-500/15 bg-transparent px-4 py-2.5 font-body text-sm text-white outline-none placeholder:text-[#405060] focus:border-blue-400/40"
        />
        <button data-testid="film-comment-send" onClick={send} disabled={busy} className="inline-flex items-center rounded-full bg-blue-600 px-4 py-2.5 text-white disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </div>
      <div className="space-y-3">
        {items.length === 0 && <p className="py-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#405060]">Aucun commentaire</p>}
        {items.map((c) => (
          <div key={c.id} className="flex gap-3" data-testid="film-comment-item">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-blue-400/30 bg-blue-500/10 font-mono text-[9px] text-blue-200">{initials(c.user_name)}</div>
            <div className="rounded-xl border border-blue-500/10 bg-blue-400/[0.02] px-4 py-2.5">
              <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-blue-300/70">{c.user_name} · {timeAgo(c.created_at)}</div>
              <p className="mt-1 font-body text-sm text-[#CBD5E1]">{c.content}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const FilmDetail = ({ filmId, onClose, onChanged }) => {
  const { user, setAuthOpen, setCredits } = useAuth();
  const navigate = useNavigate();
  const [film, setFilm] = useState(null);
  const [buying, setBuying] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await getFilm(filmId); setFilm(data); }
    catch { toast.error("Film introuvable."); onClose(); }
  }, [filmId, onClose]);
  useEffect(() => { load(); }, [load]);

  const buy = async (tier) => {
    if (!user) return setAuthOpen(true);
    try {
      setBuying(tier);
      const { data } = await buyFilm(filmId, tier);
      if (typeof data.credits === "number") setCredits(data.credits);
      toast.success(data.status === "already" ? "Déjà débloqué." : "Film débloqué !");
      await load();
      onChanged?.();
    } catch (e) {
      if (e.response?.status === 402) toast.error("Crédits insuffisants.");
      else toast.error(formatApiError(e.response?.data?.detail) || "Achat impossible.");
    } finally { setBuying(null); }
  };

  const toggleLike = async () => {
    if (!user) return setAuthOpen(true);
    try {
      const { data } = await likeFilm(filmId);
      setFilm((f) => ({ ...f, liked: data.liked, likes: data.likes }));
      onChanged?.();
    } catch (e) { console.error("toggle like failed", e); }
  };

  const remix = () => {
    if (!film?.prompt) return;
    navigate(`/?remix=${encodeURIComponent(film.prompt)}`);
  };

  const canManage = !!user && !!film && (user.user_id === film.creator_id || user.role === "admin");
  const onDelete = async () => {
    if (!window.confirm(`Supprimer définitivement « ${film.title} » ?`)) return;
    try {
      await deleteFilm(filmId);
      toast.success("Film supprimé.");
      onChanged?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Suppression impossible."); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="film-detail" className="max-h-[90vh] max-w-3xl overflow-y-auto border-blue-500/25 bg-[#0B1220]/95 p-0 backdrop-blur-2xl">
        {!film ? (
          <>
            <DialogHeader className="sr-only"><DialogTitle>Chargement du film</DialogTitle></DialogHeader>
            <div className="flex h-64 items-center justify-center"><Loader2 size={28} className="animate-spin text-blue-400" /></div>
          </>
        ) : (
          <>
            <DialogHeader className="border-b border-blue-500/15 px-6 py-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <DialogTitle className="font-display text-xl font-800 tracking-tighter text-white">{film.title}</DialogTitle>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-300/70">par {film.creator_name} · {timeAgo(film.created_at)}</p>
                </div>
                {canManage && (
                  <button data-testid="film-detail-delete" onClick={onDelete}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-red-400/40 bg-red-500/10 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-red-300 transition-colors hover:bg-red-500/25 hover:text-red-100">
                    <Trash2 size={12} /> Supprimer
                  </button>
                )}
              </div>
            </DialogHeader>

            <div className="px-6 py-5">
              {film.video_url ? (
                <video
                  data-testid="film-player"
                  src={filmVideoUrl(film.id)}
                  controls
                  autoPlay
                  className="w-full rounded-xl border border-blue-500/20 bg-black"
                />
              ) : (
                <div data-testid="film-paywall" className="relative overflow-hidden rounded-xl border border-blue-500/20 bg-[radial-gradient(circle_at_50%_40%,rgba(37,99,235,0.15),transparent_65%)] p-10 text-center">
                  {film.thumbnail && <img src={film.thumbnail} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-25 blur-sm" />}
                  <div className="relative">
                    <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-blue-400/40 bg-[#0B1220]/80"><Lock size={22} className="text-blue-200" /></span>
                    <p className="mt-4 font-display text-lg font-700 text-white">Contenu verrouillé</p>
                    <p className="mt-1 font-body text-sm text-[#8AA0C0]">Débloquez ce film pour le visionner{film.has_prompt ? " et obtenir le prompt de création" : ""}.</p>
                    <div className="mt-6 flex flex-wrap justify-center gap-3">
                      <button
                        data-testid="film-buy-watch"
                        onClick={() => buy("watch")}
                        disabled={buying}
                        className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white cta-glow transition-all duration-300 hover:bg-blue-500 disabled:opacity-60"
                      >
                        {buying === "watch" ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                        Débloquer le film · {film.watch_credits} cr
                      </button>
                      {film.premium_credits && (
                        <button
                          data-testid="film-buy-premium"
                          onClick={() => buy("premium")}
                          disabled={buying}
                          className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-200 transition-all duration-300 hover:bg-amber-400/20 disabled:opacity-60"
                        >
                          {buying === "premium" ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                          Film + prompt · {film.premium_credits} cr
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Prompt reveal (premium unlocked) */}
              {film.prompt && (
                <div className="mt-5 rounded-xl border border-amber-400/25 bg-amber-400/[0.04] p-4" data-testid="film-prompt-block">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300"><Wand2 size={13} /> Prompt de création</span>
                    <div className="flex gap-2">
                      <button data-testid="film-copy-prompt" onClick={() => { navigator.clipboard.writeText(film.prompt); toast.success("Prompt copié"); }} className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-amber-200 hover:bg-amber-400/10">
                        <Copy size={11} /> Copier
                      </button>
                      <button data-testid="film-remix" onClick={remix} className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-[#1a1200] hover:bg-amber-400">
                        <Sparkles size={11} /> Remix
                      </button>
                    </div>
                  </div>
                  <p className="whitespace-pre-wrap font-body text-sm leading-relaxed text-[#e6d9b8]">{film.prompt}</p>
                </div>
              )}

              {/* Stats + like */}
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  data-testid="film-like"
                  onClick={toggleLike}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-all duration-300 ${film.liked ? "border-blue-400/60 bg-blue-500/20 text-blue-100" : "border-blue-500/20 text-[#CBD5E1] hover:border-blue-400/40 hover:text-white"}`}
                >
                  <Heart size={13} className={film.liked ? "fill-blue-300 text-blue-300" : ""} /> {film.likes}
                </button>
                <span className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#8AA0C0]"><Eye size={13} /> {film.views}</span>
                <span className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#8AA0C0]"><MessageCircle size={13} /> {film.comments_count}</span>
                <button
                  data-testid="film-share"
                  onClick={() => shareLink(`${window.location.origin}/films?film=${film.id}`, film.title)}
                  className="ml-auto inline-flex items-center gap-2 rounded-full border border-blue-500/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#CBD5E1] transition-all duration-300 hover:border-blue-400/40 hover:text-white"
                >
                  <Share2 size={13} /> Partager
                </button>
              </div>

              <Comments filmId={film.id} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PublishModal = ({ open, onClose, onCreated }) => {
  const { user, setAuthOpen } = useAuth();
  const videoRef = useRef(null);
  const thumbRef = useRef(null);
  const [title, setTitle] = useState("");
  const [videoPath, setVideoPath] = useState(null);
  const [thumbUrl, setThumbUrl] = useState(null);
  const [price, setPrice] = useState(3);
  const [prompt, setPrompt] = useState("");
  const [premiumPrice, setPremiumPrice] = useState(5);
  const [sellPrompt, setSellPrompt] = useState(false);
  const [upV, setUpV] = useState(false);
  const [upT, setUpT] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setTitle(""); setVideoPath(null); setThumbUrl(null); setPrice(3);
    setPrompt(""); setPremiumPrice(5); setSellPrompt(false);
  };

  const pickVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("video")) return toast.error("Choisissez un fichier vidéo.");
    if (f.size > 60 * 1024 * 1024) return toast.error("Vidéo trop lourde (max 60 Mo).");
    try { setUpV(true); const { data } = await uploadFile(f); setVideoPath(data.path); toast.success("Vidéo importée."); }
    catch { toast.error("Upload impossible."); } finally { setUpV(false); }
  };
  const pickThumb = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 6 * 1024 * 1024) return toast.error("Image trop lourde (max 6 Mo).");
    try { setUpT(true); const { data } = await uploadFile(f); setThumbUrl(absFileUrl(data.path)); }
    catch { toast.error("Upload impossible."); } finally { setUpT(false); }
  };

  const submit = async () => {
    if (!user) return setAuthOpen(true);
    if (title.trim().length < 2) return toast.error("Ajoutez un titre.");
    if (!videoPath) return toast.error("Importez votre film.");
    if (sellPrompt && !prompt.trim()) return toast.error("Ajoutez le prompt à vendre.");
    if (sellPrompt && premiumPrice <= price) return toast.error("Le prix premium doit dépasser le prix du film.");
    try {
      setBusy(true);
      const payload = {
        title: title.trim(),
        video_path: videoPath,
        thumbnail: thumbUrl,
        price_usd: price,
        prompt: sellPrompt ? prompt.trim() : null,
        premium_usd: sellPrompt ? premiumPrice : null,
      };
      const { data } = await createFilm(payload);
      toast.success("Film publié dans la galerie !");
      reset();
      onCreated?.(data);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Publication impossible."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="film-publish-modal" className="max-h-[90vh] max-w-lg overflow-y-auto border-blue-500/25 bg-[#0B1220]/95 p-0 backdrop-blur-2xl">
        <DialogHeader className="border-b border-blue-500/15 px-6 py-5">
          <DialogTitle className="flex items-center gap-2 font-display text-lg font-700 tracking-tighter text-white"><Clapperboard size={18} className="text-blue-400" /> Publier un film</DialogTitle>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-300/70">Vendez votre création · vous gardez 80%</p>
        </DialogHeader>
        <div className="space-y-4 px-6 py-5">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-blue-300/70">Titre</label>
            <input data-testid="film-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140}
              placeholder="Ex : Renaissance cybernétique" className="mt-2 w-full rounded-lg border border-blue-500/25 bg-transparent px-3 py-2.5 font-body text-sm text-white outline-none placeholder:text-[#405060] focus:border-blue-400/40" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button data-testid="film-upload-video" onClick={() => videoRef.current?.click()} disabled={upV}
              className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border px-4 py-5 font-mono text-[10px] uppercase tracking-[0.15em] transition-all ${videoPath ? "border-emerald-400/40 bg-emerald-400/[0.06] text-emerald-300" : "border-blue-500/25 text-blue-200 hover:border-blue-400/40"}`}>
              {upV ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
              {videoPath ? "Vidéo importée ✓" : "Importer le film"}
            </button>
            <input ref={videoRef} type="file" accept="video/*" onChange={pickVideo} className="hidden" />
            <button data-testid="film-upload-thumb" onClick={() => thumbRef.current?.click()} disabled={upT}
              className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border px-4 py-5 font-mono text-[10px] uppercase tracking-[0.15em] transition-all ${thumbUrl ? "border-emerald-400/40 bg-emerald-400/[0.06] text-emerald-300" : "border-blue-500/25 text-blue-200 hover:border-blue-400/40"}`}>
              {upT ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
              {thumbUrl ? "Miniature ✓" : "Miniature (option)"}
            </button>
            <input ref={thumbRef} type="file" accept="image/*" onChange={pickThumb} className="hidden" />
          </div>
          {thumbUrl && <img src={thumbUrl} alt="" className="max-h-32 w-full rounded-lg border border-blue-500/15 object-cover" />}

          <div>
            <label className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-blue-300/70">
              <span>Prix du film</span><span className="text-blue-200">{price}$ · {price * 10} cr</span>
            </label>
            <input data-testid="film-price-slider" type="range" min={1} max={9} step={1} value={price} onChange={(e) => setPrice(Number(e.target.value))} className="mt-3 w-full accent-blue-500" />
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-amber-400/25 bg-amber-400/[0.04] p-4">
            <input data-testid="film-sell-prompt-toggle" type="checkbox" checked={sellPrompt} onChange={(e) => setSellPrompt(e.target.checked)} className="mt-0.5 h-4 w-4 accent-amber-400" />
            <span className="font-body text-[11px] leading-relaxed text-[#CBD5E1]">
              <Wand2 size={12} className="mr-1 inline text-amber-300" />
              Vendre aussi le <strong>prompt de création</strong> (offre premium « Film + prompt »)
            </span>
          </label>

          {sellPrompt && (
            <div className="space-y-3">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-blue-300/70">Prompt à vendre</label>
                <textarea data-testid="film-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
                  placeholder="Le prompt exact utilisé pour générer ce film…" className="mt-2 w-full resize-none rounded-lg border border-blue-500/25 bg-transparent px-3 py-2.5 font-body text-sm text-white outline-none placeholder:text-[#405060] focus:border-blue-400/40" />
              </div>
              <div>
                <label className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-blue-300/70">
                  <span>Prix « Film + prompt »</span><span className="text-amber-200">{premiumPrice}$ · {premiumPrice * 10} cr</span>
                </label>
                <input data-testid="film-premium-slider" type="range" min={1} max={20} step={1} value={premiumPrice} onChange={(e) => setPremiumPrice(Number(e.target.value))} className="mt-3 w-full accent-amber-400" />
              </div>
            </div>
          )}

          <button data-testid="film-publish-submit" onClick={submit} disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white cta-glow transition-all duration-300 hover:bg-blue-500 disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Publier le film
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Films() {
  useLenis();
  const { user, setAuthOpen } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [films, setFilms] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);

  const load = useCallback(async () => {
    try { setLoading(true); const { data } = await listFilms("new"); setFilms(data); }
    catch (e) { console.error("load films failed", e); } finally { setLoading(false); }
  }, []);
  // Re-fetch when auth hydrates so owner/purchased 'unlocked' flags are correct.
  useEffect(() => { load(); }, [load, user]);

  useEffect(() => {
    if (!user) { setRecommended([]); return; }
    recommendedFilms().then(({ data }) => setRecommended(data)).catch(() => setRecommended([]));
  }, [user]);

  // Deep link: /films?film=<id> opens the shared film directly.
  useEffect(() => {
    const fid = searchParams.get("film");
    if (fid) setOpenId(fid);
  }, [searchParams]);

  const openPublish = () => {
    if (!user) return setAuthOpen(true);
    if (!user.subscription && user.role !== "admin") {
      toast.info("Un abonnement (Débutant, Expert ou Premium) est requis pour publier un film. Vous pouvez toujours débloquer et regarder les films avec vos crédits.");
      navigate("/");
      setTimeout(() => scrollToId("pricing"), 600);
      return;
    }
    setPublishOpen(true);
  };
  const openFilm = (film) => setOpenId(film.id);

  const canManage = (f) => !!user && (user.user_id === f.creator_id || user.role === "admin");
  const onDeleteFilm = async (f) => {
    if (!window.confirm(`Supprimer définitivement « ${f.title} » ?`)) return;
    try {
      await deleteFilm(f.id);
      toast.success("Film supprimé.");
      load();
      if (user) recommendedFilms().then(({ data }) => setRecommended(data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Suppression impossible."); }
  };
  const renderFilm = (f, i, rank) => (
    <FilmCard key={f.id} film={f} index={i} onOpen={openFilm} rank={rank} canManage={canManage(f)} onDelete={onDeleteFilm} />
  );

  // Derive Netflix-style rows from a single fetch.
  const score = (f) => (f.likes || 0) * 3 + (f.views || 0) + (f.comments_count || 0);
  const trending = [...films].sort((a, b) => score(b) - score(a));
  const withPrompt = films.filter((f) => f.has_prompt);
  const spotlight = trending[0];

  return (
    <div className="relative min-h-screen bg-[#0F172A]" data-testid="films-page">
      <div className="lab-fixed-bg" aria-hidden="true" />
      <NoiseOverlay />
      <div className="relative z-10">
        <Nav />
        <main className="pb-28 pt-24 lg:pt-28">
          {loading ? (
            <div className="flex h-[70vh] items-center justify-center"><Loader2 size={30} className="animate-spin text-blue-400" /></div>
          ) : films.length === 0 ? (
            <div className="mx-auto max-w-[1400px] px-6 pt-16 lg:px-10">
              <span className="font-mono text-xs uppercase tracking-[0.35em] text-blue-400">[ Animator Film ]</span>
              <h1 className="mt-4 flex items-center gap-4 font-display text-4xl font-800 tracking-tighter text-white sm:text-5xl lg:text-6xl">
                <Clapperboard size={44} className="text-blue-400" /> Galerie de films IA
              </h1>
              <div className="mt-16 flex flex-col items-center gap-4 rounded-2xl border border-dashed border-blue-500/30 bg-blue-400/[0.02] py-20 text-center">
                <Film size={48} className="text-blue-400/40" />
                <p className="font-display text-xl font-700 text-white">Aucun film pour l'instant</p>
                <p className="font-body text-sm text-[#8AA0C0]">Soyez le premier à publier votre création IA.</p>
                <button data-testid="film-empty-publish" onClick={openPublish} className="mt-2 inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white cta-glow hover:bg-blue-500">
                  <Plus size={15} /> Publier un film
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="mx-auto max-w-[1600px] px-6 pt-6 lg:px-10">
                <Spotlight film={spotlight} onOpen={openFilm} onPublish={openPublish} />
              </div>

              <div className="mx-auto mt-8 max-w-[1600px] pl-6 lg:pl-10" data-testid="films-rows">
                {user && recommended.length > 0 && (
                  <CreatorRow title="Recommandés pour vous" icon={Sparkles} testid="films-row-reco" count={recommended.length}>
                    {recommended.map((f, i) => renderFilm(f, i))}
                  </CreatorRow>
                )}
                <CreatorRow title="Tendances" icon={TrendingUp} testid="films-row-trending" count={trending.length}>
                  {trending.slice(0, 12).map((f, i) => renderFilm(f, i, i + 1))}
                </CreatorRow>

                <CreatorRow title="Récemment ajoutés" icon={Clock} testid="films-row-new" count={films.length}>
                  {films.map((f, i) => renderFilm(f, i))}
                </CreatorRow>

                {withPrompt.length > 0 && (
                  <CreatorRow title="Avec prompt à vendre" icon={Wand2} accent="amber" testid="films-row-prompt" count={withPrompt.length}>
                    {withPrompt.map((f, i) => renderFilm(f, i))}
                  </CreatorRow>
                )}
              </div>
            </>
          )}
        </main>
        <Footer />
      </div>

      {openId && <FilmDetail filmId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
      <PublishModal open={publishOpen} onClose={() => setPublishOpen(false)} onCreated={load} />
    </div>
  );
}
